/**
 * RIM — IRA Contribution Flow (5-step wizard).
 *
 * Wave 5.2 IRA-FN-CONTRIBUTIONS rewire:
 *   The wizard no longer calls `IraCoreService.postContribution()` (which
 *   wrote TaxPlnTranAdd directly with a hardcoded TC). It now POSTs to
 *   `/v3/ira/contributions` via `client.retail.ira.postContribution(...)` —
 *   the Function performs server-side validation (plan-active → age →
 *   YTD limit → MAGI phaseout → IraTransactionCode catalog lookup) before
 *   it forwards to TaxPlnTranAdd.
 *
 *   When the IraTransactionCode catalog is empty (default v3.0 launch
 *   posture per Bill 2026-04-25 Q4/Q5), the server returns
 *   `code: 'TC_NOT_CONFIGURED'` and we render an inline admin-link.
 *
 *   The Reg E disclosure step (step 4) shows the resolved annual limit +
 *   any MAGI phaseout warning so the FSR has the figure on screen before
 *   acknowledging the disclosure.
 *
 * Steps:
 *   1. Tax year (current / prior)
 *   2. Contribution kind (11 admin-config'd kinds via IraContributionKind)
 *   3. Amount + funding source + optional MAGI / filing-status / source acct
 *   4. Reg E disclosure ack — surfaces server-side limit + MAGI warning
 *   5. Review → POST /v3/ira/contributions → success / TC-not-configured banner
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from '@retail-ops/panel-shell/fi';
import { Button, CardRadioGroup, KVPair, Pill, Stepper, WidgetCard } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type {
  IraContributionKind,
  IraContributionFundingSource,
  IraFilingStatus,
  IraContributionRequest,
  IraContributionPostedData,
  IraMagiPhaseoutWarning,
  IraContributionErrorCode,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

type Funding = IraContributionFundingSource;

interface ContribDraft {
  taxYear: number;
  kind: IraContributionKind;
  amount: string;
  funding: Funding;
  filingStatus: IraFilingStatus;
  magi: string;
  sourceAccount: string;
  officerNotes: string;
  regEAck: boolean;
}

const STEPS = [
  { label: 'Tax year' },
  { label: 'Contribution kind' },
  { label: 'Amount & source' },
  { label: 'Reg E disclosure' },
  { label: 'Review & submit' },
];

const KIND_OPTIONS: Array<{
  value: IraContributionKind;
  primary: string;
  helper: string;
}> = [
  { value: 'TRADITIONAL_REGULAR',         primary: 'Traditional — regular',          helper: 'Annual Trad-IRA contribution within the regular / catch-up cap.' },
  { value: 'ROTH_REGULAR',                primary: 'Roth — regular',                 helper: 'Annual Roth contribution; subject to MAGI phaseout.' },
  { value: 'TRADITIONAL_TRANSFER_IN',     primary: 'Traditional — transfer in',      helper: 'Trustee-to-trustee transfer into a Trad-IRA.' },
  { value: 'ROTH_TRANSFER_IN',            primary: 'Roth — transfer in',             helper: 'Trustee-to-trustee transfer into a Roth IRA.' },
  { value: 'SEP_EMPLOYER',                primary: 'SEP — employer contribution',    helper: 'Employer-side SEP-IRA deposit; participant-age check skipped.' },
  { value: 'SIMPLE_EMPLOYEE',             primary: 'SIMPLE — employee deferral',     helper: 'Employee elective deferral into a SIMPLE IRA.' },
  { value: 'SIMPLE_EMPLOYER_MATCH',       primary: 'SIMPLE — employer match',        helper: 'Employer-side SIMPLE match.' },
  { value: 'SIMPLE_EMPLOYER_NONELECTIVE', primary: 'SIMPLE — employer non-elective', helper: 'Employer non-elective SIMPLE contribution.' },
  { value: 'ROTH_CONVERSION',             primary: 'Roth conversion',                helper: 'Convert pre-tax Trad/SEP/SIMPLE balances to a Roth IRA.' },
  { value: 'RECHARACTERIZATION',          primary: 'Recharacterization',             helper: 'Recharacterize a prior contribution to the opposite plan.' },
  { value: '529_TO_ROTH',                 primary: '529 → Roth (Secure 2.0)',        helper: 'Beneficiary 529-to-Roth rollover under Secure Act 2.0 §126.' },
];

const FILING_STATUS_OPTIONS: ReadonlyArray<{ value: IraFilingStatus; label: string }> = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MFJ',    label: 'Married filing jointly' },
  { value: 'MFS',    label: 'Married filing separately' },
  { value: 'HOH',    label: 'Head of household' },
  { value: 'QW',     label: 'Qualifying widow(er)' },
];

export function ContributionFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<ContribDraft>({
    taxYear: currentYear,
    kind: 'TRADITIONAL_REGULAR',
    amount: '',
    funding: 'CHECK',
    filingStatus: 'SINGLE',
    magi: '',
    sourceAccount: '',
    officerNotes: '',
    regEAck: false,
  });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<IraContributionErrorCode | null>(null);
  const [postedData, setPostedData] = useState<IraContributionPostedData | null>(null);

  // Contribution-limit display (advisory only — server enforces).
  const contributionLimit = useMemo<{ baseLimit: string; catchUpLimit?: string; catchUpMinAge?: number } | null>(() => {
    const rows = (config.iraConfig ?? []).filter((r) => r.configType === 'contribution-limit' && r.contributionLimit);
    if (rows.length === 0) return null;
    const planType = ira?.planCode ?? 'TRAD';
    const exact = rows.find((r) => r.contributionLimit?.year === draft.taxYear && r.contributionLimit?.planType === planType);
    const pick = exact ?? rows.find((r) => r.contributionLimit?.year === draft.taxYear) ?? rows[0];
    const limit = pick?.contributionLimit;
    if (!limit) return null;
    return {
      baseLimit: limit.baseLimit,
      ...(limit.catchUpLimit ? { catchUpLimit: limit.catchUpLimit } : {}),
      ...(limit.catchUpMinAge !== undefined ? { catchUpMinAge: limit.catchUpMinAge } : {}),
    };
  }, [config.iraConfig, draft.taxYear, ira?.planCode]);

  if (!planNumber) {
    return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting a contribution." />;
  }
  if (loadingIra) {
    return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  }
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const amountNum = Number.parseFloat(draft.amount);
  const magiNum = draft.magi.trim() === '' ? null : Number.parseFloat(draft.magi);
  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return Number.isInteger(draft.taxYear) && draft.taxYear >= 1900;
      case 1: return !!draft.kind;
      case 2:
        if (!Number.isFinite(amountNum) || amountNum <= 0) return false;
        if (!draft.funding) return false;
        if (draft.magi.trim() !== '' && !Number.isFinite(magiNum as number)) return false;
        return true;
      case 3: return draft.regEAck === true;
      case 4: return submitState === 'idle' || submitState === 'error';
      default: return false;
    }
  })();

  const officerCode =
    user?.officerCode
    ?? (typeof user?.upn === 'string' ? user.upn : undefined)
    ?? '';

  const submit = async (): Promise<void> => {
    if (!officerCode) {
      setSubmitError('Officer code missing from session — re-authenticate before posting.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    try {
      const body: IraContributionRequest = {
        planNumber: ira.planNumber,
        taxYear: draft.taxYear,
        kind: draft.kind,
        amount: amountNum,
        fundingSource: draft.funding,
        officerCode,
        ...(draft.sourceAccount ? { sourceAccount: draft.sourceAccount } : {}),
        ...(draft.officerNotes ? { officerNotes: draft.officerNotes } : {}),
        ...(magiNum !== null && Number.isFinite(magiNum) ? { magi: magiNum as number } : {}),
        ...(draft.filingStatus ? { filingStatus: draft.filingStatus } : {}),
        ...(ira.ownerBirthDate ? { ownerBirthDate: ira.ownerBirthDate } : {}),
      };

      const res = await client.retail.ira.postContribution(body);

      if (!res.success) {
        setSubmitError(res.error);
        setSubmitErrorCode(res.code);
        setSubmitState('error');
        return;
      }

      setPostedData(res.data);
      emit({
        eventType: 'ira-contribution-posted',
        eventCategory: 'cif-account',
        severity: 'info',
        context: `plan:${ira.planNumber}`,
        payload: {
          planNumber: ira.planNumber,
          planCode: ira.planCode,
          kind: draft.kind,
          scenario: res.data.scenario,
          tranCode: res.data.transactionCode,
          amount: amountNum,
          taxYear: draft.taxYear,
          funding: draft.funding,
          runningYtd: res.data.runningYtdContribution ?? null,
          remaining: res.data.regulatoryLimitRemaining ?? null,
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Contribution post failed.');
      setSubmitErrorCode('POST_FAILED');
      setSubmitState('error');
    }
  };

  // Pre-render the MAGI warning rendered on Reg E + Review steps.
  const renderMagiWarning = (warning: IraMagiPhaseoutWarning | null | undefined): JSX.Element | null => {
    if (!warning) return null;
    const isWarn =
      warning.status === 'above_window'
      || warning.status === 'in_window';
    return (
      <div className={isWarn ? 'rim-rmd-banner is-warn' : 'rim-rmd-banner'} role={isWarn ? 'alert' : 'status'}>
        <strong>MAGI phaseout</strong> ({warning.filingStatus}) — window ${warning.start.toLocaleString()}{' → '}${warning.end.toLocaleString()}.{' '}
        {warning.verbalAffirmRequired
          ? 'No MAGI on file — FSR must verbally affirm eligibility per banking practice.'
          : `Status: ${warning.status.replace('_', ' ')}.`}
        {warning.notes ? <> &middot; {warning.notes}</> : null}
      </div>
    );
  };

  const renderErrorBanner = (): JSX.Element | null => {
    if (!submitError) return null;
    if (submitErrorCode === 'TC_NOT_CONFIGURED') {
      return (
        <div className="rim-rmd-banner is-warn" role="alert">
          <strong>Transaction code not configured.</strong> Please contact your admin to seed the transaction-code sequence for this contribution kind.{' '}
          <a href="#/admin/ira/transaction-codes" onClick={(e) => { e.preventDefault(); navigate('/admin/ira/transaction-codes'); }}>
            Go to Admin → IRA → Transaction Codes
          </a>
          .
          <div className="rim-flow__error-detail">{submitError}</div>
        </div>
      );
    }
    if (submitErrorCode === 'LIMIT_EXCEEDED' || submitErrorCode === 'INELIGIBLE_AGE') {
      return <div className="rim-rmd-banner is-warn" role="alert">{submitError}</div>;
    }
    if (submitErrorCode === 'MAGI_PHASEOUT_EXCEEDED') {
      return <div className="rim-rmd-banner is-warn" role="alert"><strong>MAGI phaseout exceeded.</strong> {submitError}</div>;
    }
    if (submitErrorCode === 'PLAN_INACTIVE' || submitErrorCode === 'PLAN_NOT_FOUND') {
      return <div className="rim-rmd-banner is-warn" role="alert"><strong>Plan unavailable.</strong> {submitError}</div>;
    }
    if (submitErrorCode === 'LIMIT_NOT_CONFIGURED') {
      return (
        <div className="rim-rmd-banner is-warn" role="alert">
          <strong>Contribution limit not configured.</strong> {submitError}
        </div>
      );
    }
    return <div className="rim-rmd-banner is-warn" role="alert">{submitError}</div>;
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>
          IRA contribution · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}
        </h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Tax year" stripe="compliance">
          <p className="rim-flow__helper">
            Pick the tax year this contribution applies to. Prior-year contributions must be posted before
            the federal tax deadline. Server-side validation enforces YTD vs the configured limit.
          </p>
          <div className="rim-flow__year-row">
            {[currentYear, currentYear - 1].map((y) => (
              <Button
                key={y}
                variant={draft.taxYear === y ? 'primary' : 'ghost'}
                onClick={() => setDraft({ ...draft, taxYear: y })}
              >
                {y}
              </Button>
            ))}
          </div>
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Contribution kind" stripe="compliance">
          <CardRadioGroup<IraContributionKind>
            name="rim-contrib-kind"
            selected={draft.kind}
            onChange={(v) => setDraft({ ...draft, kind: v })}
            options={KIND_OPTIONS}
          />
          {ira.planCode !== 'SEP' && draft.kind === 'SEP_EMPLOYER' && (
            <div className="rim-rmd-banner is-warn" role="alert">
              This plan is not a SEP — SEP_EMPLOYER contributions will be rejected by Core Director.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Amount & funding source" stripe="compliance">
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Funding source</span>
              <select
                className="rim-benef-field"
                value={draft.funding}
                onChange={(e) => setDraft({ ...draft, funding: e.currentTarget.value as Funding })}
                aria-label="Funding source"
              >
                <option value="CHECK">Check</option>
                <option value="CHECK_RECEIVED">Check received</option>
                <option value="ACH">ACH</option>
                <option value="WIRE">Wire</option>
                <option value="INTERNAL_TRANSFER">Internal transfer</option>
              </select>
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Source account (optional)</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.sourceAccount}
                placeholder="e.g. demand-deposit account #"
                onChange={(e) => setDraft({ ...draft, sourceAccount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Filing status (MAGI lookup)</span>
              <select
                className="rim-benef-field"
                value={draft.filingStatus}
                onChange={(e) => setDraft({ ...draft, filingStatus: e.currentTarget.value as IraFilingStatus })}
                aria-label="Filing status"
              >
                {FILING_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Modified AGI (optional)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.magi}
                min={0}
                step={0.01}
                placeholder="Leave blank for verbal affirm"
                onChange={(e) => setDraft({ ...draft, magi: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field rim-flow__field--full">
              <span className="rim-flow__field-label">Officer notes (optional)</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.officerNotes}
                onChange={(e) => setDraft({ ...draft, officerNotes: e.currentTarget.value })}
              />
            </label>
          </div>
          <div className="rim-flow__hint">
            <strong>Configured limit:</strong>{' '}
            {contributionLimit
              ? `${contributionLimit.baseLimit} base${contributionLimit.catchUpLimit ? ` · ${contributionLimit.catchUpLimit} catch-up (age ${contributionLimit.catchUpMinAge ?? '—'}+)` : ''}`
              : '— (Function will reject if not seeded server-side).'}
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Reg E disclosure" stripe="compliance">
          <p className="rim-flow__helper">
            Confirm the owner has received and acknowledged the Reg E / IRA Contribution Eligibility
            disclosure. Server-side validation has not yet fired — full limit / MAGI evaluation happens
            on submit.
          </p>
          <label className="rim-flow__ack-row">
            <input
              type="checkbox"
              checked={draft.regEAck}
              onChange={(e) => setDraft({ ...draft, regEAck: e.currentTarget.checked })}
            />
            <span>
              I confirm the IRA owner received the PCB Reg E / Contribution-Eligibility disclosure and has
              acknowledged it either electronically or on paper.
            </span>
          </label>
        </WidgetCard>
      )}

      {step === 4 && (
        <WidgetCard title="Review & submit" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="Owner CIF" value={ira.cifId ?? '—'} />
            <KVPair label="Tax year" value={draft.taxYear} />
            <KVPair label="Kind" value={draft.kind} />
            <KVPair label="Amount" value={`$${amountNum.toFixed(2)}`} />
            <KVPair label="Funding" value={draft.funding} />
            <KVPair label="Filing status" value={draft.filingStatus} />
            <KVPair label="MAGI" value={magiNum !== null ? `$${(magiNum as number).toFixed(2)}` : <Pill variant="warn">verbal affirm</Pill>} />
            <KVPair label="Reg E ack" value={draft.regEAck ? 'Yes' : <Pill variant="warn">Missing</Pill>} />
          </div>

          {submitState === 'submitted' && postedData && (
            <>
              <div className="rim-rmd-banner" role="status">
                Contribution posted via /v3/ira/contributions. Transaction id: <code>{postedData.transactionId}</code>
                {' '}(scenario {postedData.scenario}, TC {postedData.transactionCode}).
              </div>
              <div className="rim-kv-grid rim-flow__post-summary">
                {postedData.runningYtdContribution !== null && postedData.runningYtdContribution !== undefined && (
                  <KVPair label="Running YTD" value={`$${postedData.runningYtdContribution.toFixed(2)}`} />
                )}
                {postedData.regulatoryLimitRemaining !== null && postedData.regulatoryLimitRemaining !== undefined && (
                  <KVPair label="Limit remaining" value={`$${postedData.regulatoryLimitRemaining.toFixed(2)}`} />
                )}
              </div>
              {renderMagiWarning(postedData.magiPhaseoutWarning)}
            </>
          )}

          {renderErrorBanner()}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button variant="primary" disabled={submitState === 'submitting'} onClick={() => { void submit(); }}>
            {submitState === 'submitting' ? 'Posting…' : 'Submit contribution'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
